import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { globalVars } from "./global/GlobalVars.js";
import axios from "axios";

const Editor = observer(() => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const blinkIntervalRef = useRef(null);
  const inputRefs = useRef([]);
  inputRefs.current = [];

  const currentPageConfig = globalVars.pageProperties.currentPageConfig;
  const generalKeys = ["pageTitle", "forward", "backward", "showStartImage", "mobile"];
  const orderedElementKeys = [
    "name",
    "type",
    "left/top",
    "width/height",
    "right",
    "bottom",
    "transform",
    "backgroundColor",
    "activeColor",
    "opacity",
    "borderStyle",
    "borderWidth",
    "borderColor",
    "borderRadius",
    "color",
    "textFontSize",
    "textColor",
    "fontWeight",
    "textHorizontalAlignment",
    "textVerticalAlignment",
    "imgSrc",
    "path",
    "text",
    "states",
    "variable",
    "valueToWrite",
    "interfaceProps",
    "visibility",
    "index",
    "bit",
    "textArrayIndex",
    "varIsReference",
    "pathIsPassed",
    "visibilityIsPassed",
    "stateIsPassed",
    "textIsTextArray",
    "authRead",
    "authWrite",
  ];

  const registerInputRef = (ref) => {
    if (ref && !inputRefs.current.includes(ref)) {
      inputRefs.current.push(ref);
    }
  };

  const cleanPageConfigBeforeSave = (originalConfig) => {
    const clone = JSON.parse(JSON.stringify(originalConfig));
    if (Array.isArray(clone.elements)) {
      clone.elements.forEach((el) => {
        delete el.blink;
        delete el.blinkOpacity;
      });
    }

    return clone;
  };

  const savePageConfig = async () => {
    try {
      const pageName = globalVars.pageProperties.actualPath;
      const rawConfig = globalVars.pageProperties.currentPageConfig;
      const configToSave = cleanPageConfigBeforeSave(rawConfig);
      const url =
        window.location.port === "3000"
          ? `http://${window.location.hostname}:${globalVars.httpPort}/project/save`
          : `/project/save`;

      await axios.post(url, { page: pageName, config: configToSave });

      alert("✅ Konfiguration erfolgreich gespeichert!");
    } catch (err) {
      console.error("Fehler beim Speichern der Konfiguration", err);
      alert("❌ Speichern fehlgeschlagen!");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      if ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) {
        if (e.key.toLowerCase() === "s") {
          e.preventDefault();
          savePageConfig();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const elements = currentPageConfig.elements;
    if (!Array.isArray(elements)) return;

    // Reset all
    elements.forEach((el) => {
      el.blink = false;
      el.blinkOpacity = 1;
    });

    if (expandedIndex !== null && elements[expandedIndex]) {
      elements[expandedIndex].blink = true;

      let on = false;
      blinkIntervalRef.current = setInterval(() => {
        if (elements[expandedIndex]) {
          elements[expandedIndex].blinkOpacity = on ? 1 : 0.3;
          on = !on;
        }
      }, 500);
    }

    return () => {
      clearInterval(blinkIntervalRef.current);
    };
  }, [expandedIndex]);

  const handleGeneralChange = (key, value) => {
    if (key === "showStartImage") {
      currentPageConfig[key] = value === "true";
    } else {
      try {
        currentPageConfig[key] = JSON.parse(value);
      } catch {
        currentPageConfig[key] = value;
      }
    }
  };

  const handleElementChange = (index, key, value) => {
    const element = currentPageConfig.elements[index];
    try {
      element[key] = JSON.parse(value);
    } catch {
      element[key] = value;
    }
  };

  const renderEditableCell = (el, index, key) => {
    const getNumberAndUnit = (value) => {
      const match = String(value).match(/^(-?\d+(?:\.\d+)?)([a-z%]*)$/i);
      return match ? [parseFloat(match[1]), match[2]] : [0, ""];
    };

    const handleTabKey = (e) => {
      if (e.key === "Tab") {
        e.preventDefault();
        const dir = e.shiftKey ? -1 : 1;
        const i = inputRefs.current.indexOf(e.target);
        const next = inputRefs.current[i + dir];
        if (next) next.focus();
      }
    };

    const updateCombinedValue = (combinedStr, fields) => {
      const parts = combinedStr.split(/[\/x]/).map((s) => s.trim());
      if (parts.length !== fields.length) return;
      fields.forEach((field, i) => {
        const match = parts[i].match(/^(-?\d+(?:\.\d+)?)([a-z%]*)$/i);
        if (match) {
          const [_, num, unit] = match;
          handleElementChange(index, field, `${num}${unit}`);
        }
      });
    };

    if (key === "left/top") {
      const [leftVal, leftUnit] = getNumberAndUnit(el.left);
      const [topVal, topUnit] = getNumberAndUnit(el.top);
      const display = `${leftVal}${leftUnit} / ${topVal}${topUnit}`;

      return (
        <input
          style={{ width: "100%" }}
          value={display}
          ref={registerInputRef}
          onChange={(e) => updateCombinedValue(e.target.value, ["left", "top"])}
          onKeyDown={(e) => {
            handleTabKey(e);
            let step = e.shiftKey ? 10 : e.ctrlKey ? 100 : 1;

            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              e.preventDefault();
              const delta = e.key === "ArrowRight" ? step : -step;
              handleElementChange(
                index,
                "left",
                `${leftVal + delta}${leftUnit}`,
              );
            }

            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              const delta = e.key === "ArrowDown" ? step : -step;
              handleElementChange(index, "top", `${topVal + delta}${topUnit}`);
            }
          }}
        />
      );
    }

    if (key === "width/height") {
      const [wVal, wUnit] = getNumberAndUnit(el.width);
      const [hVal, hUnit] = getNumberAndUnit(el.height);
      const display = `${wVal}${wUnit} x ${hVal}${hUnit}`;

      return (
        <input
          style={{ width: "100%" }}
          value={display}
          ref={registerInputRef}
          onChange={(e) =>
            updateCombinedValue(e.target.value, ["width", "height"])
          }
          onKeyDown={(e) => {
            handleTabKey(e);
            let step = e.shiftKey ? 10 : e.ctrlKey ? 100 : 1;

            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              e.preventDefault();
              const delta = e.key === "ArrowRight" ? step : -step;
              handleElementChange(index, "width", `${wVal + delta}${wUnit}`);
            }
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              const delta = e.key === "ArrowDown" ? step : -step;
              handleElementChange(index, "height", `${hVal + delta}${hUnit}`);
            }
          }}
        />
      );
    }

    const rawValue = key in el ? (el[key] ?? "") : "";
    const valueStr =
      typeof rawValue === "object"
        ? JSON.stringify(rawValue)
        : String(rawValue);

    return (
      <input
        style={{ width: "100%" }}
        ref={registerInputRef}
        value={valueStr}
        onChange={(e) => handleElementChange(index, key, e.target.value)}
        onKeyDown={handleTabKey}
      />
    );
  };

  const cellStyle = {
    border: "1px solid #ccc",
    padding: "8px",
    backgroundColor: "#fff",
  };

  const headerStyle = {
    ...cellStyle,
    backgroundColor: "#f2f2f2",
    fontWeight: "bold",
  };

  const renderGeneralTable = () => (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "30px",
      }}
    >
      <thead>
        <tr>
          <th style={headerStyle}>Key</th>
          <th style={headerStyle}>Value</th>
        </tr>
      </thead>
      <tbody>
        {generalKeys.map((key) => (
          <tr key={key}>
            <td style={cellStyle}>{key}</td>
            <td style={cellStyle}>
              <input
                style={{ width: "100%" }}
                ref={registerInputRef}
                value={
                  Array.isArray(currentPageConfig[key])
                    ? JSON.stringify(currentPageConfig[key])
                    : String(currentPageConfig[key] ?? "")
                }
                onChange={(e) => handleGeneralChange(key, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Tab") {
                    e.preventDefault();
                    const dir = e.shiftKey ? -1 : 1;
                    const i = inputRefs.current.indexOf(e.target);
                    const next = inputRefs.current[i + dir];
                    if (next) next.focus();
                  }
                }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderElementSummary = () => {
    const elements = currentPageConfig?.elements;
    if (!Array.isArray(elements)) return <p>Keine Elemente vorhanden.</p>;

    return (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={headerStyle}>Name</th>
            <th style={headerStyle}>Type</th>
          </tr>
        </thead>
        <tbody>
          {elements.map((el, index) => (
            <React.Fragment key={index}>
              <tr
                onClick={() =>
                  setExpandedIndex(expandedIndex === index ? null : index)
                }
                style={{
                  cursor: "pointer",
                  backgroundColor:
                    expandedIndex === index ? "#e6f7ff" : "#fafafa",
                  transition: "background 0.2s",
                }}
              >
                <td style={cellStyle}>{el.name ?? ""}</td>
                <td style={cellStyle}>{el.type ?? ""}</td>
              </tr>
              {expandedIndex === index && (
                <tr>
                  <td
                    colSpan={2}
                    style={{ padding: 0, backgroundColor: "#f9f9f9" }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr>
                          <th style={headerStyle}>Key</th>
                          <th style={headerStyle}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderedElementKeys.map((key) => (
                          <tr key={key}>
                            <td style={cellStyle}>{key}</td>
                            <td style={cellStyle}>
                              {renderEditableCell(el, index, key)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div
      style={{
        position: "absolute",
        top: "200px",
        left: "100px",
        width: "95%",
        height: "600px",
        backgroundColor: "lightgrey",
        overflowY: "auto",
        padding: "20px",
        fontFamily: "Segoe UI, sans-serif",
        fontSize: "14px",
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ marginBottom: "10px" }}>🧾 Seiteneigenschaften</h2>
      {renderGeneralTable()}

      <h2 style={{ marginTop: "30px", marginBottom: "10px" }}>📦 Elemente</h2>
      {renderElementSummary()}

      <div style={{ marginTop: "30px", textAlign: "right" }}>
        <button
          onClick={savePageConfig}
          style={{
            padding: "10px 20px",
            fontSize: "14px",
            backgroundColor: "#007acc",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          💾 Speichern
        </button>
      </div>
    </div>
  );
});

export default Editor;
